import React, { useEffect, useState } from 'react';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    List,
    ListItemButton,
    ListItemText,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import { fetchAssetUsage } from '../../api/api';

/**
 * Lists the entries whose media fields use an asset. Rich-text embedded images
 * are a separate upload path and don't appear here.
 */
const AssetUsageDialog = ({ open, onClose, asset, workspaceId }) => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [usages, setUsages] = useState([]);
    const [pagination, setPagination] = useState(null);
    const [loadingMore, setLoadingMore] = useState(false);

    useEffect(() => {
        if (!open || !asset?._id) return;
        let cancelled = false;
        setLoading(true);
        setError(null);
        setUsages([]);
        setPagination(null);
        fetchAssetUsage(asset._id, 1)
            .then((res) => {
                if (cancelled) return;
                setUsages(res.data?.usages ?? []);
                setPagination(res.data?.pagination ?? null);
            })
            .catch((err) => {
                if (!cancelled) setError(err.response?.data?.message || 'Could not load usage');
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [open, asset?._id]);

    const loadMore = () => {
        if (!pagination?.hasNextPage) return;
        setLoadingMore(true);
        fetchAssetUsage(asset._id, pagination.page + 1)
            .then((res) => {
                setUsages((prev) => [...prev, ...(res.data?.usages ?? [])]);
                setPagination(res.data?.pagination ?? null);
            })
            .catch((err) => setError(err.response?.data?.message || 'Could not load usage'))
            .finally(() => setLoadingMore(false));
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle sx={{ pr: 6 }}>
                Used in{pagination?.total ? ` (${pagination.total})` : ''}
                <Typography variant="body2" color="text.secondary" noWrap>
                    {asset?.title || asset?.fileName}
                </Typography>
                <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }} aria-label="close">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                {loading ? (
                    <Box display="flex" justifyContent="center" py={3}>
                        <CircularProgress size={28} />
                    </Box>
                ) : error && usages.length === 0 ? (
                    <Typography color="error">{error}</Typography>
                ) : usages.length === 0 ? (
                    <Typography color="text.secondary">This asset isn't used in any entry's media field.</Typography>
                ) : (
                    <List disablePadding>
                        {usages.map((usage) => (
                            <ListItemButton
                                key={usage.entryId}
                                onClick={() => {
                                    onClose();
                                    navigate(`/${workspaceId}/edit-entry/${usage.entryId}`);
                                }}
                            >
                                <ListItemText
                                    primary={usage.identifier || `Entry ${usage.entryId}`}
                                    secondary={`${usage.contentTypeName} · ${usage.fields.map((f) => f.name).join(', ')}`}
                                />
                                <Box display="flex" gap={1} ml={1} flexShrink={0}>
                                    <Chip size="small" variant="outlined" label={usage.environmentName} />
                                    <Chip
                                        size="small"
                                        color={usage.published ? 'success' : 'default'}
                                        label={usage.published ? 'Published' : 'Draft'}
                                    />
                                </Box>
                            </ListItemButton>
                        ))}
                        {pagination?.hasNextPage && (
                            <Box display="flex" justifyContent="center" pt={1}>
                                <Button onClick={loadMore} disabled={loadingMore}>
                                    {loadingMore ? 'Loading…' : `Load more (${pagination.total - usages.length} left)`}
                                </Button>
                            </Box>
                        )}
                        {error && <Typography color="error" sx={{ pt: 1 }}>{error}</Typography>}
                    </List>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default AssetUsageDialog;
